import { useEffect, useRef, useState } from 'react'

import { usePermissions } from 'commons/customHooks/useAuth'
import useDebounce from 'commons/customHooks/useDebounce'
import i18n from 'commons/libs/i18n'
import { percentualValueMask } from 'commons/utils/mask'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { DownloadLink, DSInputType, Info, Input, Modal, Search, Select } from 'components'
import type { ActionPerm } from 'components/Structures/Header/constants'
import { type CustomBoolean, materialLineOptions, TypesValidations } from 'constants/enums'
import {
  decimalToString,
  handleDiscountInput,
} from 'container/CreateOrder/components/TopSection/utils'
import type { SalesOrderFieldsInterface } from 'container/SalesOrder/interfaces'
import { Controller, useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { postMaintainDraft } from 'services/draft'
import type { RequestBodyMaintainDraft } from 'services/draft/interfaces'
import { getListMaterialOrder } from 'services/product'
import { type MaterialOrderResponse, setDefaultParams } from 'store/ProductContext'
import {
  getItems,
  setDraftBody,
  setMaterialSearch,
  setMaterialsLimitExceed,
} from 'store/SalesOrderContext/salesOrderSlice'
import { useDispatch, useSelector } from 'store/store'

import { MaximumMaterialsLimit, ModelSpreadsheetUrl } from './constants'
import { ColumnDivStyled, Divider, FieldWrapper, RowDivStyled, TitleCol } from './styles'

type InsertProp = { user: string; hasUnsavedOVConfig: boolean; hasOrderNumber: boolean }
function Insert({ user, hasOrderNumber }: InsertProp) {
  const dispatch = useDispatch()
  const {
    draftBody,
    emptyData,
    secureRecords,
    records,
    materialSearch,
    loadingMaterials: loading,
    isInternationalOrder,
    isSampleOrder,
  } = useSelector((state) => state.salesOrder)
  const { isYTRA, isHAVAConectaOrder } = useSelector(({ ov }) => ov)
  const { focusMaterialInsertInput } = useSelector((state) => state.structure)
  const { defaultParams } = useSelector((state) => state.products)
  const [searchValue, setSearchValue] = useState('')
  const [searchPage, setSearchPage] = useState<number>(0)
  const [modalMaterial, setModalMaterial] = useState<boolean>(false)
  const methods = useFormContext<SalesOrderFieldsInterface>()
  const { getValues, setValue, control, reset } = methods
  const { verifyPermission } = usePermissions()
  const searchRef = useRef<HTMLInputElement>(null)

  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action) || loading
  }

  const formProductPathFromDescription = (codigo: string) => {
    return codigo
      .toLocaleLowerCase()
      .replace('- ', '')
      .replace(/ /g, '-')
      .replace(/(\.|\/)/g, '')
  }
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const request = {
    cd_canl_dstb: draftBody?.cd_canl_dstb,
    cd_empr: draftBody?.cd_empr,
    cd_escr_vend: draftBody?.cd_escr_vend,
    cd_orgn_vend: draftBody?.cd_orgn_vend,
    cd_seto_atvd: draftBody?.cd_seto_atvd,
    cd_tipo_orde: getValues('cd_tipo_orde'),
    dt_dsjd_pedi: draftBody?.dt_dsjd_pedi,
    fl_matr_2qua: TypesValidations.NO,
    fl_matr_fora_linh: getValues('fl_matr_fora_linh'),
    fl_mto_prep: draftBody?.fl_mto_prep,
    nr_pagi: '0',
    nr_pedi_rasc: draftBody?.nr_pedi_rasc,
  }

  const requestProductPage = (material: MaterialOrderResponse) => {
    if (secureRecords?.length >= MaximumMaterialsLimit) {
      dispatch(setMaterialsLimitExceed(true))
    } else {
      const materialPath = formProductPathFromDescription(material.cd_matr)
      navigate(`${pathname}/${materialPath}`)
    }
  }

  const handleMaterialSearch = (inputValue: string, page: string) => {
    const returnApi: Promise<MaterialOrderResponse[]> = getListMaterialOrder({
      ...request,
      cd_matr: inputValue,
      nr_pagi: page,
      qt_rowcount: 15,
    })

    return returnApi
  }

  useEffect(() => {
    setSearchValue(materialSearch)
  }, [materialSearch])
  const dispatchSearch = (value: string) => {
    dispatch(setMaterialSearch(value))
    dispatch(getItems('', value))
  }
  const debounceSearch = useDebounce(dispatchSearch, 1000)
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase()
    setSearchValue(value)
    debounceSearch(value)
  }

  const handleMaterialSelection = (e: MaterialOrderResponse) => {
    if (e.cd_cent_matr === null || !e.cd_cent_matr.trim()) {
      setModalMaterial(true)
      return
    }
    localStorage.setItem('material', JSON.stringify(e))

    const clie: Record<string, string> = getValues('cd_clie') as unknown as Record<string, string>

    const form = getValues()
    const {
      cd_seto_atvd,
      cd_tipo_orde,
      fl_mto_prep,
      pc_dsct,
      fl_prmt_desd,
      cd_cond_pgto,
      ds_cond_pgto,
      nr_pedi_clie,
      nr_dias_adic,
      address,
      cd_clie_entr,
      nr_pedi_rasc,
      dt_ano_mes_cota,
      dt_dsjd_pedi,
      ds_text_nota_fisc,
      fl_matr_fora_linh,
      ds_text_item_mto,
      cd_moti_prom,
      cd_catg_item,
      cd_elem_pep,
      cd_cent_cust,
      fl_data_fixa_entr,
    } = form

    const bodyMaintainRascunho: RequestBodyMaintainDraft = {
      ...draftBody,
      cd_catg_item,
      cd_cent_cust,
      cd_clie: clie.cd_clie || '',
      cd_clie_entr: address || cd_clie_entr || '',
      cd_cond_pgto,
      cd_elem_pep,
      cd_moti_prom,
      cd_seto_atvd: cd_seto_atvd || '',
      cd_tipo_orde,
      cd_usua_alte: user,
      ds_cond_pgto,
      ds_item_pedi_rasc: [],
      ds_text_item_mto,
      ds_text_nota_fisc: ds_text_nota_fisc,
      dt_ano_mes_cota,
      dt_dsjd_pedi,
      fl_data_fixa_entr,
      fl_dsct_matr: TypesValidations.YES,
      fl_matr_fora_linh,
      fl_mto_prep,
      fl_prmt_desd,
      nr_dias_adic,
      nr_pedi_clie,
      nr_pedi_cpra: '',
      nr_pedi_rasc: nr_pedi_rasc || '',
      pc_dsct,
    }
    reset(getValues())
    postMaintainDraft(bodyMaintainRascunho).then(() => {
      dispatch(
        setDraftBody({
          ...draftBody,
          cd_catg_item: cd_catg_item ?? '',
          cd_cent_cust: cd_cent_cust ?? '',
          cd_cond_pgto,
          cd_elem_pep: cd_elem_pep ?? '',
          cd_moti_prom: cd_moti_prom ?? '',
          cd_seto_atvd,
          cd_tipo_orde,
          ds_cond_pgto: ds_cond_pgto ?? '',
          ds_text_item_mto,
          ds_text_nota_fisc: ds_text_nota_fisc,
          dt_ano_mes_cota,
          dt_dsjd_pedi,
          fl_data_fixa_entr: (fl_data_fixa_entr as CustomBoolean) || TypesValidations.NO,
          fl_matr_fora_linh: fl_matr_fora_linh as CustomBoolean,
          fl_mto_prep,
          fl_prmt_desd: fl_prmt_desd as CustomBoolean,
          nr_dias_adic,
          nr_pedi_clie,
          pc_dsct: pc_dsct ?? 0,
        }),
      )

      dispatch(
        setDefaultParams({
          ...defaultParams,
          cd_cent_matr:
            draftBody.fl_pedi_antp === TypesValidations.YES ? '' : e.cd_cent_matr.split(',')[0],
          cd_matr: e.cd_matr,
          cd_tipo_orde: cd_tipo_orde,
          fl_matr_fora_linh: fl_matr_fora_linh as CustomBoolean,
        }),
      )
      requestProductPage(e)
    })
  }

  const renderOrderDiscount = () => {
    return (
      <div>
        <Controller
          control={control}
          name="pc_dsct"
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Input
              addorment
              design={DSInputType.PERCENT_INPUT}
              disabled={
                draftBody.totalizador.qt_tota_item_soli > 0 || isYTRA || isInternationalOrder
              }
              errorOutline={!!error?.message}
              InputProps={{
                fullWidth: true,
                inputProps: { maxLength: 5 },
              }}
              id="discount"
              label={i18n.t('components.insertMaterial.discount')}
              labelAdornment={
                <Info iconSize={12}>
                  {i18n.t('components.insertMaterial.discountFieldTooltip')}
                </Info>
              }
              maxLength={5}
              name={name}
              onBlur={(e) => {
                const currentValue = getValues('pc_dsct') as unknown as number
                const pc_dsct = handleDiscountInput(e.target.value, currentValue)
                setValue('pc_dsct', pc_dsct)
                setValue('fl_dsct_matr', TypesValidations.YES)
              }}
              onChange={(e) => {
                onChange(percentualValueMask(e.target.value))
              }}
              value={decimalToString(value as number)}
            />
          )}
        />
      </div>
    )
  }

  return (
    <ColumnDivStyled>
      <RowDivStyled>
        <TitleCol>
          <span id="MaterialsInsertTitle">Inserção de material</span>
          <span id="MaterialsInsertSubtitle">
            Após preencher o cabeçalho selecione, especifique e solicite os materiais
          </span>
        </TitleCol>

        <DownloadLink
          content="Baixar planilha modelo"
          disabled={isHAVAConectaOrder}
          options={[{ label: 'Modelo Novo (.xlsx)', url: ModelSpreadsheetUrl }]}
        />
      </RowDivStyled>

      {hasOrderNumber && (
        <>
          <Divider />
          <FieldWrapper
            mediaTemplateColumnsMD={'.8fr 1.2fr 0 .5fr 1.22fr'}
            templateColumns={'130px 1.8fr 1.5fr 90px 1fr'}
          >
            <Controller
              control={control}
              name="fl_matr_fora_linh"
              render={({ field }) => (
                <Select
                  disabled={
                    loading ||
                    !!searchValue ||
                    !!records.length ||
                    draftBody.fl_pedi_antp === TypesValidations.YES ||
                    isHAVAConectaOrder
                  }
                  label="Produção"
                  name={field.name}
                  onChange={(value) => {
                    field.onChange(value)
                  }}
                  options={materialLineOptions}
                  value={field.value}
                />
              )}
            />
            <Search<MaterialOrderResponse>
              buttonSelectOption={false}
              clearAfterSelect
              columnsSizeOption={['100%']}
              disabled={shouldDisable(ACTIONS_SCREENS.EDIT) || isHAVAConectaOrder}
              flexWrapOptionDescription={false}
              flexWrapRowOption
              keysColumnsOption={['ds_matr']}
              label="Adicionar material"
              labelsConcat={['ds_matr', 'cd_matr']}
              loadOptions={handleMaterialSearch}
              minimumInput={3}
              optionsLimit={15}
              optionsMaxHeight="300px"
              page={searchPage}
              placeHolder="Busque por código ou nome"
              setOptionSelected={handleMaterialSelection}
              setPage={setSearchPage}
              shouldFocusInput={focusMaterialInsertInput}
              titlesColumnsOption={[]}
              type="select"
              valueCase="upper"
            />
            <span />
            {isSampleOrder ? <span /> : renderOrderDiscount()}
            <span>
              <Input
                addorment
                design={DSInputType.SEARCH_INPUT}
                disabled={!materialSearch && emptyData}
                InputProps={{ placeholder: 'Busque por código' }}
                label="Localizar material"
                onChange={handleSearch}
                ref={searchRef}
                value={searchValue}
              />
            </span>
          </FieldWrapper>
        </>
      )}

      <Modal
        buttonText="Entendi"
        description="A venda deste produto foi interrompida, consulte o back office para mais informações"
        open={modalMaterial}
        setOpen={setModalMaterial}
        title="Material indisponível"
      />
    </ColumnDivStyled>
  )
}

export default Insert
